/** Enter a US Nutrition Facts panel and see what its numbers can really mean (21 CFR 101.9 rounding). */
import { useMemo, useState, type ReactElement } from 'react';
import {
  LABEL_VERDICT_HEADLINE, LESS_THAN_ONE, analyzeLabel, citationUrl,
  type DeclaredGrams, type DeclaredLabel, type TrueLabelReport, type ValueInterval,
} from '@engine/tef';

/** The form as typed; gram fields accept "<1". */
export interface LabelDraft {
  calories: string;
  fat: string;
  carbs: string;
  protein: string;
  fiber: string;
  sugarAlcohol: string;
  servingSize: string;
  servings: string;
}

export const EMPTY_LABEL: LabelDraft = { calories: '', fat: '', carbs: '', protein: '', fiber: '', sugarAlcohol: '', servingSize: '', servings: '' };

const EXAMPLES: ReadonlyArray<{ name: string; draft: LabelDraft }> = [
  { name: 'Almonds (1 oz)', draft: { calories: '160', fat: '14', carbs: '6', protein: '6', fiber: '3', sugarAlcohol: '', servingSize: '28', servings: '16' } },
  { name: '"0 calorie" spray', draft: { calories: '0', fat: '0', carbs: '0', protein: '0', fiber: '', sugarAlcohol: '', servingSize: '0.25', servings: '550' } },
  { name: 'Protein bar', draft: { calories: '210', fat: '7', carbs: '23', protein: '20', fiber: '10', sugarAlcohol: '4', servingSize: '60', servings: '1' } },
  { name: 'Label that under-counts', draft: { calories: '100', fat: '5', carbs: '20', protein: '8', fiber: '', sugarAlcohol: '', servingSize: '', servings: '' } },
];

type Parsed<T> = { ok: true; value: T } | { ok: false; error: string };

function parseNumber(s: string, name: string, required: boolean): Parsed<number | null> {
  const t: string = s.trim();
  if (t === '') return required ? { ok: false, error: `${name} is required.` } : { ok: true, value: null };
  const v: number = Number(t);
  if (!Number.isFinite(v) || v < 0) return { ok: false, error: `${name} must be a number of 0 or more.` };
  return { ok: true, value: v };
}

function parseGrams(s: string, name: string, required: boolean): Parsed<DeclaredGrams | null> {
  const t: string = s.trim().replace(/\s*g$/i, '');
  if (t === '<1' || t.toLowerCase() === 'less than 1') return { ok: true, value: LESS_THAN_ONE };
  return parseNumber(t, name, required);
}

/**
 * FDA 21 CFR 101.9(c) never prints calories between 0 and 5, fat between 0 and 0.5 g, or other gram
 * values between 0 and 1 (those read "<1 g"). Such numbers cannot come off a real US label.
 */
function offGrid(p: DeclaredLabel): string | null {
  if (p.caloriesKcal > 0 && p.caloriesKcal < 5) return 'US labels print under 5 calories as 0. Enter 0 or check the label.';
  if (p.fatG > 0 && p.fatG < 0.5) return 'US labels print under 0.5 g of fat as 0. Enter 0 or check the label.';
  const grams: ReadonlyArray<[string, DeclaredGrams | null | undefined]> = [
    ['Total carbohydrate', p.totalCarbG], ['Protein', p.proteinG], ['Fiber', p.fiberG], ['Sugar alcohols', p.sugarAlcoholG],
  ];
  for (const [name, v] of grams) {
    if (typeof v === 'number' && v > 0 && v < 1) return `${name}: US labels print under 1 g as "<1" (or 0 under 0.5 g). Enter <1 or 0.`;
  }
  return null;
}

/** Draft to engine input, or the first problem found. */
export function parseLabelDraft(d: LabelDraft): Parsed<DeclaredLabel> {
  const calories = parseNumber(d.calories, 'Calories', true);
  const fat = parseNumber(d.fat, 'Total fat', true);
  const carbs = parseGrams(d.carbs, 'Total carbohydrate', true);
  const protein = parseGrams(d.protein, 'Protein', true);
  const fiber = parseGrams(d.fiber, 'Fiber', false);
  const polyol = parseGrams(d.sugarAlcohol, 'Sugar alcohols', false);
  const serving = parseNumber(d.servingSize, 'Serving size', false);
  const servings = parseNumber(d.servings, 'Servings per container', false);
  for (const p of [calories, fat, carbs, protein, fiber, polyol, serving, servings]) if (!p.ok) return p;
  if (!calories.ok || !fat.ok || !carbs.ok || !protein.ok || !fiber.ok || !polyol.ok || !serving.ok || !servings.ok) return { ok: false, error: 'Check the form.' };
  const label: DeclaredLabel = {
    caloriesKcal: calories.value as number,
    fatG: fat.value as number,
    totalCarbG: carbs.value as DeclaredGrams,
    proteinG: protein.value as DeclaredGrams,
    fiberG: fiber.value,
    sugarAlcoholG: polyol.value,
    servingSizeG: serving.value,
    servingsPerContainer: servings.value,
  };
  const grid: string | null = offGrid(label);
  return grid === null ? { ok: true, value: label } : { ok: false, error: grid };
}

/** The analysis, or the reason it could not run; never throws during render. */
export function safeAnalyze(label: DeclaredLabel): { report: TrueLabelReport } | { error: string } {
  try {
    return { report: analyzeLabel(label) };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'These numbers could not be analysed.' };
  }
}

const kcal = (x: number): string => (Math.abs(x - Math.round(x)) < 0.05 ? Math.round(x).toString() : x.toFixed(1));
const span = (v: ValueInterval): string => `${kcal(v.low)}–${kcal(v.high)}`;

const VERDICT_CLASS: Readonly<Record<TrueLabelReport['verdict'], string>> = {
  CONSISTENT: 'info',
  CONSISTENT_WITH_FIBER_ADJUSTMENT: 'info',
  LABEL_BELOW_MACROS: 'warn',
  LABEL_ABOVE_MACROS: 'warn',
  BETWEEN_METHODS: 'info',
};

/** Label calories vs what the macros allow, on one axis. */
function IntervalChart({ report }: { report: TrueLabelReport }): ReactElement {
  const rows: ReadonlyArray<{ name: string; v: ValueInterval; color: string }> = [
    { name: 'Printed calories', v: report.caloriesInterval, color: 'var(--energy)' },
    { name: 'Macros, 4-4-9', v: report.methodBKcal, color: 'var(--protein)' },
    { name: 'Macros, fiber-adjusted', v: report.methodCKcal, color: 'var(--carbs)' },
  ];
  const lo: number = Math.min(...rows.map((r) => r.v.low));
  const hi: number = Math.max(...rows.map((r) => r.v.high));
  const pad: number = Math.max(1, (hi - lo) * 0.08);
  const min: number = Math.max(0, lo - pad);
  const max: number = hi + pad;
  const pct = (x: number): number => ((x - min) / (max - min)) * 100;
  return (
    <div className="ivl" role="img" aria-label="Printed calories compared with what the printed macros allow">
      {rows.map((r) => (
        <div key={r.name} className="ivl-row">
          <span className="ivl-name">{r.name}</span>
          <div className="ivl-track"><b style={{ left: `${pct(r.v.low)}%`, width: `${Math.max(1.2, pct(r.v.high) - pct(r.v.low))}%`, background: r.color }} /></div>
          <span className="ivl-val num">{span(r.v)} kcal</span>
        </div>
      ))}
    </div>
  );
}

export function TrueLabelReportView({ report }: { report: TrueLabelReport }): ReactElement {
  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className={`note ${VERDICT_CLASS[report.verdict]}`}>
        <b>{LABEL_VERDICT_HEADLINE[report.verdict]}</b>
        {report.unexplainedGapKcal > 0 && <div>Unexplained gap: at least {kcal(report.unexplainedGapKcal)} kcal per serving.</div>}
      </div>
      <IntervalChart report={report} />
      <dl className="kv tef-kv">
        <dt>Best estimate from the macros</dt><dd className="num">{kcal(report.macroBestEstimateKcal)} kcal</dd>
        <dt>FDA acts only above</dt><dd className="num">{kcal(report.legalCeilingKcal)} kcal</dd>
        {report.zeroCalorieHiddenMaxPerServing !== null && <><dt>"0 calories" could hide</dt><dd className="num">up to {kcal(report.zeroCalorieHiddenMaxPerServing)} kcal</dd></>}
        {report.zeroCalorieHiddenMaxPerContainer !== null && <><dt>Whole container</dt><dd className="num">up to {kcal(report.zeroCalorieHiddenMaxPerContainer)} kcal</dd></>}
      </dl>
      <ul className="tef-findings">{report.findings.map((f) => <li key={f}>{f}</li>)}</ul>
      <ol className="cite" style={{ paddingLeft: 18, margin: 0 }}>
        {report.citations.map((c) => {
          const url: string | null = citationUrl(c);
          return <li key={c.shortRef}>{c.shortRef}: {c.finding} {url !== null && <a href={url} target="_blank" rel="noreferrer">link</a>}</li>;
        })}
      </ol>
    </div>
  );
}

export function TrueLabelChecker({ initial, title }: { initial?: LabelDraft; title?: string }): ReactElement {
  const [d, setD] = useState<LabelDraft>(initial ?? EMPTY_LABEL);
  const set = (k: keyof LabelDraft, v: string): void => setD((x) => ({ ...x, [k]: v }));
  const parsed = useMemo(() => parseLabelDraft(d), [d]);
  const analysis = useMemo(() => (parsed.ok ? safeAnalyze(parsed.value) : null), [parsed]);
  const report: TrueLabelReport | null = analysis !== null && 'report' in analysis ? analysis.report : null;
  const touched: boolean = Object.values(d).some((v) => v.trim() !== '');

  const field = (k: keyof LabelDraft, label: string, unit: string, hint?: string): ReactElement => (
    <label className="field"><span>{label}</span>
      <div className="input-suffix"><input className="input" inputMode="decimal" value={d[k]} placeholder={hint} onChange={(e) => set(k, e.target.value)} /><em>{unit}</em></div>
    </label>
  );

  return (
    <div className="stack" style={{ gap: 14 }}>
      {title !== undefined && <h3 style={{ margin: 0 }}>{title}</h3>}
      <div className="row" style={{ gap: 6 }}>
        <span className="faint" style={{ fontSize: 13, fontWeight: 600 }}>Try:</span>
        {EXAMPLES.map((e) => <button key={e.name} type="button" className="btn btn-sm btn-ghost" onClick={() => setD(e.draft)}>{e.name}</button>)}
        {touched && <button type="button" className="btn btn-sm btn-ghost" onClick={() => setD(EMPTY_LABEL)}>Clear</button>}
      </div>
      <div className="form-grid">
        {field('calories', 'Calories', 'kcal')}
        {field('fat', 'Total fat', 'g')}
        {field('carbs', 'Total carbohydrate', 'g', '<1 is OK')}
        {field('protein', 'Protein', 'g', '<1 is OK')}
        {field('fiber', 'Dietary fiber', 'g', 'optional')}
        {field('sugarAlcohol', 'Sugar alcohols', 'g', 'optional')}
        {field('servingSize', 'Serving size', 'g', 'optional')}
        {field('servings', 'Servings per container', '×', 'optional')}
      </div>
      {touched && !parsed.ok && <div className="note warn">{parsed.error}</div>}
      {analysis !== null && 'error' in analysis && <div className="note warn">{analysis.error}</div>}
      {report !== null && <TrueLabelReportView report={report} />}
      {!touched && <p className="faint" style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Copy the numbers exactly as printed for one serving. Leave optional rows empty if the label doesn’t list them.</p>}
    </div>
  );
}
